import { formatMoneyForLocale } from '@stitch-fix/i18n';
import { fireEvent } from '@stitch-fix/kufak-gtm-client';
import trackProductEvent from '../../utils/trackProductEvent';
import SubSourceType from '../../helpers/SubSourceType';

const formattedTrackingSkusFromGraphQL = (
  inStockBagItems,
  outOfStockBagItems,
) => {
  const formatItem = item => ({
    sku_id: item.id,
    source: item.source,
    collection: item.collection,
    quantity: item.quantity,
    limited_stock: item.limitedStock,
    outfit_id: item.metadata?.outfit?.id,
    kept_sku_id: item.metadata?.keptSku?.id,
  });

  const formattedInStockSkus = inStockBagItems.map(item => ({
    ...formatItem(item),
    out_of_stock: false,
  }));

  const formattedOutOfStockSkus = outOfStockBagItems.map(item => ({
    ...formatItem(item),
    out_of_stock: true,
  }));

  return [...formattedInStockSkus, ...formattedOutOfStockSkus];
};

const formattedTrackingSkus = (inStockBagItems, outOfStockBagItems) => {
  const formatItem = item => ({
    sku_id: item.sku.sku_id,
    source: item.source,
    collection: item.collection,
    quantity: item.quantity,
    limited_stock: item.limited_stock,
    outfit_id: item.metadata.outfit_id,
    kept_sku_id: item.metadata.kept_sku_id,
    ...(Object.prototype.hasOwnProperty.call(
      item,
      'similar_items_link_visible',
    ) && { similar_items_link_visible: item.similar_items_link_visible }),
  });

  const formattedInStockSkus = inStockBagItems.map(item => ({
    ...formatItem(item),
    out_of_stock: false,
  }));

  const formattedOutOfStockSkus = outOfStockBagItems.map(item => ({
    ...formatItem(item),
    out_of_stock: true,
  }));

  return [...formattedInStockSkus, ...formattedOutOfStockSkus];
};

const ShoppingBagTracker = new (function () {
  const eventName = 'sf.clients.product_tracking.event';

  this.trackShoppingBagSimilarItemPDPViewEvent = ({
    skuId,
    externalProductId,
    source,
    relatedItemsSkuIds,
  }) => {
    return trackProductEvent({
      event_name: 'shopping_bag.similar_item_pdp',
      canonical_name: 'pdp',
      action_type: 'view',
      action_name: 'pdp_view',
      sku_id: skuId,
      external_product_id: externalProductId,
      source,
      sub_source: SubSourceType.SIMILAR_ITEMS,
      collection: null,
      similar_items: relatedItemsSkuIds,
    });
  };

  // Note that canonicalName should be shopping_bag if a client clicks the "+" from the bag.
  // Otherwise it should be pdp
  this.trackAddToBagEvent = ({
    skuId,
    externalProductId,
    canonicalName,
    source,
    collectionName,
    keptSkuId,
    outfitId,
    relatedItemSkuIds,
    subtotal,
    subSource,
    locale,
  }) => {
    if (!skuId || !canonicalName) {
      return null;
    }

    fireEvent({
      eventName: 'add_to_cart',
      eventAttributes: {
        sku_id: skuId,
        external_product_id: externalProductId,
        revenue: formatMoneyForLocale(locale, subtotal, { subunits: true }),
      },
    });

    return trackProductEvent({
      event_name: 'shopping_bag.add_to_bag',
      canonical_name: canonicalName,
      action_type: 'click',
      action_name: 'add_to_bag',
      skus: [
        {
          sku_id: skuId,
          external_product_id: externalProductId,
          source,
          collection: collectionName,
          kept_sku_id: keptSkuId,
          outfit_id: outfitId,
          sub_source: subSource,
        },
      ],
      related_item_sku_ids: relatedItemSkuIds,
      similar_items: relatedItemSkuIds,
    });
  };

  // although it seems counterintuitive to have a "shopping bag buy now"
  // tracking event, we need to track if people that are accessing these PDPs
  // from the shopping bag (i.e. similar items in bag) are adding to bag or
  // immediately checking out with the item
  this.trackBuyNowEvent = ({
    skuId,
    externalProductId,
    source,
    keptSkuId,
    relatedItemSkuIds,
    subSource,
  }) => {
    if (!skuId) {
      return null;
    }

    trackProductEvent({
      event_name: 'shopping_bag.buy_now',
      canonical_name: 'pdp',
      action_type: 'click',
      action_name: 'buy_now',
      skus: [
        {
          sku_id: skuId,
          external_product_id: externalProductId,
          source,
          collection: null,
          kept_sku_id: keptSkuId,
          sub_source: subSource,
        },
      ],
      related_item_sku_ids: relatedItemSkuIds,
      similar_items: relatedItemSkuIds,
    });

    return null;
  };

  this.trackViewBagEvent = (inStockBagItems, outOfStockBagItems) => {
    // all_items_out_of_stock, may be true, false, or nil where nil represents an empty bag.
    let allItemsOutOfStock = inStockBagItems.length === 0;

    if (inStockBagItems.length === 0 && outOfStockBagItems.length === 0) {
      allItemsOutOfStock = null;
    }

    return trackProductEvent({
      event_name: eventName,
      canonical_name: 'shopping_bag',
      action_type: 'view',
      action_name: 'page_view',
      skus: formattedTrackingSkus(inStockBagItems, outOfStockBagItems),
      all_items_out_of_stock: allItemsOutOfStock,
    });
  };

  this.trackRemoveBagItemEvent = (inStockBagItems, outOfStockBagItems) => {
    return trackProductEvent({
      event_name: eventName,
      canonical_name: 'shopping_bag',
      action_type: 'click',
      action_name: 'remove_item',
      skus: formattedTrackingSkus(inStockBagItems, outOfStockBagItems),
    });
  };

  // We are defining cart created when an item is added to an empty bag.
  // For context: a client can have many carts created. This happens when
  // they remove all items and add again OR purchase and their cart is
  // cleared and they add to bag after that.
  this.trackBagCreatedEvent = (bagId, inStockBagItems, outOfStockBagItems) => {
    return trackProductEvent({
      event_name: eventName,
      canonical_name: 'shopping_bag',
      action_type: 'event',
      action_name: 'bag_created',
      bag_id: bagId,
      skus: formattedTrackingSkusFromGraphQL(
        inStockBagItems,
        outOfStockBagItems,
      ),
    });
  };

  this.trackBagIconClickEvent = canonical => {
    return trackProductEvent({
      event_name: eventName,
      canonical_name: canonical,
      action_type: 'click',
      action_name: 'bag_icon_click',
    });
  };

  this.trackBagPDPClickEvent = (
    skuId,
    source,
    collection,
    outfitId,
    keptSkuId,
    externalProductId,
  ) => {
    trackProductEvent({
      event_name: eventName,
      canonical_name: 'shopping_bag',
      action_type: 'click',
      action_name: 'clicked_into_pdp',
      skus: [
        {
          sku_id: skuId,
          external_product_id: externalProductId,
          source,
          collection,
          outfit_id: outfitId,
          kept_sku_id: keptSkuId,
        },
      ],
    });
  };

  this.trackCreditBannerViewEvent = () => {
    return trackProductEvent({
      event_name: 'shopping_bag',
      canonical_name: 'shopping_bag',
      action_type: 'view',
      action_name: 'credit_banner_view',
    });
  };
})();

export default ShoppingBagTracker;
